import React from 'react';
import PropTypes from 'prop-types';
import format from 'string-template';
import { connect } from 'react-redux';
import { change, untouch } from 'redux-form';
import { toPlainObject, isEmpty, toArray } from 'lodash';
import { length, required } from 'redux-form-validators';

import {
    NAME_LENGTH,
    FIRSTNAME_LENGTH,
    CONTACT_MESSAGE_LENGTH,
    FILE_ACCEPTED_FORMAT
} from '../../../constants/form';
import { REDIRECT_SAV_CLIENT } from '../../../constants/index';
import { MOMENTJS_DATE_FORMAT_REVERSED } from '../../../constants/moment';
import { MIN_DAYS, MAX_DAYS } from '../../../services/contactrequest/constants';

import { fetcheFAQByPartnerId } from '../../../services/ebon/actions';
import { sendContactRequest, reset } from '../../../services/contactrequest/actions';

import { getI18nByApp } from '../../../services/i18n/selector';

import {
    toDate,
    nowTimestampMinusDays,
    toTimestamp,
    stringToMoment
} from '../../../utils/dateUtils';
import { getClickId, getUserId } from '../../../utils/plebicardUtils';
import { convertHtmlToReactDomWithParams, removeListMarker } from '../../../utils/htmlUtils';

import {
    DateInputFieldComponent,
    EmailInputFieldComponent,
    TextInputFieldComponent,
    NumberInputFieldComponent,
    HourInputFieldComponent,
    SelectFieldComponent,
    TextareaFieldComponent,
    FilesInputFieldComponent,
    LegendFieldComponent
} from '../../../components/form';
import { SuccessInfoComponent } from '../../form/SuccessInfoComponent';

import { ContactRequestStyledFormContainer } from './ContactRequestFormComponentStyle';

const FORM_NAME = 'contactrequest-form';

const resetFields = {
    lastname: '',
    firstname: '',
    email: ''
};

const getContactRequestReasonsToOptions = (data = []) =>
    data.map(item => ({ label: item.label, value: item.id }));

const getPartnersOptions = (data = []) => data.map(item => ({ label: item.name, value: item.id }));

class ContactRequestFormComponent extends React.Component {
    constructor(props) {
        super(props);
        this.state = { isComplaint: false, isPartnerSelected: false };
    }

    componentWillReceiveProps(nextProps) {
        if (this.props.isAuthed && !nextProps.isAuthed) {
            this.resetFields(FORM_NAME, resetFields);
        }
    }

    submit = data => {
        const { userId, sendContactRequest, reset } = this.props;

        const clickId = getClickId();

        if (this.state.isComplaint) {
            const datetime = data.purchaseDate + ' ' + data.time;

            const momentDate = stringToMoment(datetime);
            data.purchaseDate = toTimestamp(momentDate);
        }
        sendContactRequest(data, userId, clickId, () => {
            setTimeout(() => {
                reset();
            }, 5000);
        });
    };

    getUserInfo() {
        const { isAuthed, initialValues } = this.props;
        if (isAuthed) {
            return initialValues;
        }
    }

    handleOnChangeReason = e => {
        const { reasons } = this.props;

        const value = Number(e.target.value);
        const reason = reasons.find(r => r.id === value);

        this.setState({ isComplaint: reason && reason.userLoginState === 'LOGGED' });
    };

    handleOnChangePartner = e => {
        const value = e.target.value;
        if (value > 0) {
            const { fetcheFAQByPartnerId } = this.props;
            this.setState({ isPartnerSelected: true });
            fetcheFAQByPartnerId(value);
        }
    };

    resetFields = (formName, fieldsObj) => {
        Object.keys(fieldsObj).forEach(fieldKey => {
            //reset the field's value
            this.props.change(formName, fieldKey, fieldsObj[fieldKey]);

            //reset the field's error
            this.props.untouch(formName, fieldKey);
        });
    };

    render() {
        const {
            i18n,
            i18nFeatureForm,
            isAuthed,
            requestSubmit,
            errorSubmit,
            successSubmit,
            contactRequestReasonsOptions = [],
            partnersOption = [],
            errorMessage,
            faqEbon
        } = this.props;
        const { isComplaint, isPartnerSelected } = this.state;

        if (!successSubmit) {
            if (REDIRECT_SAV_CLIENT) {
                return (
                    <p className="information-message full">
                        {convertHtmlToReactDomWithParams(
                            getI18nByApp(
                                i18n,
                                'page.CONTACTREQUEST.INFORMATION_MESSAGE_SAV_REDIRECTION'
                            ),
                            REDIRECT_SAV_CLIENT
                        )}
                    </p>
                );
            }
            return (
                <ContactRequestStyledFormContainer
                    form={FORM_NAME}
                    classNameInputContainer="grid-2 has-gutter"
                    onSubmit={this.submit}
                    initialValues={this.getUserInfo()}
                    encType
                    requestSubmit={requestSubmit}
                    errorSubmit={errorSubmit}
                    submitText={getI18nByApp(i18n, 'page.CONTACTREQUEST.BUTTON_SUBMIT')}>
                    <EmailInputFieldComponent
                        name="email"
                        label={getI18nByApp(i18n, 'page.CONTACTREQUEST.LABEL_EMAIL')}
                        placeholder={getI18nByApp(i18n, 'page.CONTACTREQUEST.PLACEHOLDER_EMAIL')}
                        classNameInputContainer="mr50"
                        isRequired
                        readOnly={isAuthed}
                    />
                    <TextInputFieldComponent
                        name="lastname"
                        label={getI18nByApp(i18n, 'page.CONTACTREQUEST.LABEL_LASTNAME')}
                        placeholder={getI18nByApp(i18n, 'page.CONTACTREQUEST.PLACEHOLDER_LASTNAME')}
                        isRequired
                        readOnly={isAuthed}
                        classNameInputContainer="one-half"
                        validate={[
                            required({ msg: getI18nByApp(i18n, 'feature.form.REQUIRED') }),
                            length({
                                max: NAME_LENGTH,
                                msg: format(
                                    getI18nByApp(i18n, 'feature.form.LASTNAME_SIZE_ERROR'),
                                    NAME_LENGTH
                                )
                            })
                        ]}
                    />
                    <TextInputFieldComponent
                        name="firstname"
                        label={getI18nByApp(i18n, 'page.CONTACTREQUEST.LABEL_FIRSTNAME')}
                        placeholder={getI18nByApp(
                            i18n,
                            'page.CONTACTREQUEST.PLACEHOLDER_FIRSTNAME'
                        )}
                        isRequired
                        readOnly={isAuthed}
                        classNameInputContainer="one-half"
                        validate={[
                            required({ msg: getI18nByApp(i18n, 'feature.form.REQUIRED') }),
                            length({
                                max: FIRSTNAME_LENGTH,
                                msg: format(
                                    getI18nByApp(i18n, 'feature.form.FIRSTNAME_SIZE_ERROR'),
                                    FIRSTNAME_LENGTH
                                )
                            })
                        ]}
                    />
                    <SelectFieldComponent
                        name="requestReasonId"
                        defaultOptionLabel={getI18nByApp(
                            i18n,
                            'page.CONTACTREQUEST.DEFAULT_OPTION_LABEL'
                        )}
                        isRequired
                        options={contactRequestReasonsOptions}
                        classNameInputContainer="one-half mr50"
                        validate={[
                            required({
                                msg: getI18nByApp(
                                    i18n,
                                    'page.CONTACTREQUEST.SELECT_REQUIRED_MESSAGE'
                                )
                            })
                        ]}
                        onChange={this.handleOnChangeReason}
                    />

                    {isComplaint && (
                        <React.Fragment>
                            <DateInputFieldComponent
                                name="purchaseDate"
                                label={getI18nByApp(
                                    i18n,
                                    'page.CONTACTREQUEST.LABEL_PURCHASE_DATE'
                                )}
                                classNameInputContainer="one-half"
                                isRequired
                                mustBeDatePast
                                max={toDate(
                                    nowTimestampMinusDays(MIN_DAYS),
                                    MOMENTJS_DATE_FORMAT_REVERSED
                                )}
                                min={toDate(
                                    nowTimestampMinusDays(MAX_DAYS),
                                    MOMENTJS_DATE_FORMAT_REVERSED
                                )}
                                key={0}
                            />

                            <HourInputFieldComponent
                                name="time"
                                label={getI18nByApp(
                                    i18n,
                                    'page.CONTACTREQUEST.LABEL_PURCHASE_HOUR'
                                )}
                                classNameInputContainer="one-half mr50"
                                isRequired
                            />

                            <SelectFieldComponent
                                name="partnerId"
                                label={getI18nByApp(
                                    i18n,
                                    'page.CONTACTREQUEST.LABEL_PURCHASE_PARTNER'
                                )}
                                defaultOptionLabel={getI18nByApp(
                                    i18n,
                                    'page.CONTACTREQUEST.DEFAULT_OPTION_LABEL_PARTNER'
                                )}
                                isRequired
                                options={partnersOption}
                                classNameInputContainer="one-half mr50"
                                validate={[
                                    required({
                                        msg: getI18nByApp(
                                            i18n,
                                            'page.CONTACTREQUEST.SELECT_REQUIRED_MESSAGE'
                                        )
                                    })
                                ]}
                                onChange={this.handleOnChangePartner}
                            />

                            <NumberInputFieldComponent
                                name="amount"
                                classNameInputContainer="one-half mr50"
                                placeholder={getI18nByApp(i18n, 'feature.form.AMOUNT_EBON')}
                                label={getI18nByApp(i18n, 'feature.form.AMOUNT_EBON_PLACEHOLDER')}
                                isRequired
                                validate={[
                                    length({
                                        max: 6,
                                        msg: getI18nByApp(i18n, 'feature.form.NUMBER_INVALID')
                                    })
                                ]}
                                i18nFeatureForm={i18nFeatureForm}
                            />

                            {isPartnerSelected &&
                                faqEbon.length > 0 &&
                                faqEbon.filter(f => f.conditions).length > 0 && (
                                    <LegendFieldComponent classNameInputContainer="full conditions">
                                        <h3>
                                            {getI18nByApp(
                                                i18n,
                                                'page.CONTACTREQUEST.LABEL_PURCHASE_CONDITIONS'
                                            )}
                                        </h3>
                                        {faqEbon
                                            .filter(f => f.conditions)
                                            .map(f =>
                                                convertHtmlToReactDomWithParams(
                                                    removeListMarker(f.content)
                                                )
                                            )}
                                    </LegendFieldComponent>
                                )}
                        </React.Fragment>
                    )}
                    <FilesInputFieldComponent
                        name="files"
                        nameForm={FORM_NAME}
                        btText={getI18nByApp(i18n, 'page.CONTACTREQUEST.BUTTON_CHOOSE_FILE')}
                        formatAccepted={FILE_ACCEPTED_FORMAT}
                        maxSize={2}
                        isMultiple
                        classNameInputContainer="full"
                    />
                    <TextareaFieldComponent
                        name="message"
                        label={getI18nByApp(i18n, 'page.CONTACTREQUEST.LABEL_MESSAGE')}
                        isRequired
                        classNameInputContainer="full"
                        validate={[
                            required({
                                msg: getI18nByApp(
                                    i18n,
                                    'page.CONTACTREQUEST.TEXT_AREA_REQUIRED_MESSAGE'
                                )
                            }),
                            length({
                                max: CONTACT_MESSAGE_LENGTH,
                                msg: format(
                                    getI18nByApp(
                                        i18n,
                                        'page.CONTACTREQUEST.MESSAGE_SIZE_ERROR',
                                        'page.CONTACTREQUEST.CONTACT_MESSAGE_LENGTH'
                                    )
                                )
                            })
                        ]}
                    />
                    <LegendFieldComponent classNameInputContainer="full">
                        {getI18nByApp(i18n, 'page.CONTACTREQUEST.LEGEND_MANDATORY_FIELDS')}
                    </LegendFieldComponent>
                    {errorMessage && (
                        <LegendFieldComponent classNameInputContainer="color-err">
                            {errorMessage}
                        </LegendFieldComponent>
                    )}
                </ContactRequestStyledFormContainer>
            );
        } else {
            return (
                <SuccessInfoComponent>
                    <p>{getI18nByApp(i18n, 'page.CONTACTREQUEST.SUBMIT_SUCCEEDED')}</p>
                </SuccessInfoComponent>
            );
        }
    }
}

ContactRequestFormComponent.propTypes = {
    i18n: PropTypes.object.isRequired,
    contactRequestReasonsOptions: PropTypes.array.isRequired
};

function mapStateToProps(state) {
    const user = toPlainObject(state.account.user);

    return {
        i18n: state.i18n,
        i18nFeatureForm: state.i18n.feature.form,
        errorMessage: state.contactrequest.error,
        requestSubmit: state.contactrequest.fetching || false,
        errorSubmit: !isEmpty(state.contactrequest.errorMessage),
        successSubmit: state.contactrequest.successContactRequestSubmit,
        fetching: state.contactrequest.fetching,
        reasons: state.contactrequest.contactrequestreasons,
        contactRequestReasonsOptions: getContactRequestReasonsToOptions(
            state.contactrequest.contactrequestreasons
        ),
        partnersOption: getPartnersOptions(toArray(state.partner.partners)),
        isAuthed: !isEmpty(state.authentication.token),
        initialValues: {
            lastname: user.lastname ? user.lastname : '',
            firstname: user.firstname ? user.firstname : '',
            email: user.email ? user.email : ''
        },
        faqEbon: state.ebon.faq || [],
        userId: getUserId(state)
    };
}

const mapDispatchtoProps = {
    sendContactRequest,
    reset,
    change,
    untouch,
    fetcheFAQByPartnerId
};

export default connect(mapStateToProps, mapDispatchtoProps)(ContactRequestFormComponent);



// WEBPACK FOOTER //
// ./src/components/pages/contactrequest/ContactRequestFormComponent.js